import { normalizeMediaUrl } from "~/utils/normalizeMediaUrl";

const videoExtensions = new Set(["mp4", "webm", "mov", "m4v", "ogg"]);
const pdfExtensions = new Set(["pdf"]);

const parseJsonArray = (value: unknown) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parseNumber = (value: unknown) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value !== "string") return 0;

  const parsed = Number(value.replace(/[\s\u00A0]/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveMediaFileName = (value: unknown) => {
  const normalized = normalizeMediaUrl(value);
  if (!normalized) return "";

  const cleanPath = normalized.split("#")[0]?.split("?")[0] ?? normalized;
  const fileName = cleanPath.split("/").filter(Boolean).at(-1) ?? "";
  if (!fileName) return "";

  try {
    return decodeURIComponent(fileName).toLowerCase();
  } catch {
    return fileName.toLowerCase();
  }
};

const resolveMediaExtension = (value: unknown) => {
  const fileName = resolveMediaFileName(value);
  const dotIndex = fileName.lastIndexOf(".");

  if (dotIndex === -1) return "";

  return fileName.slice(dotIndex + 1).toLowerCase();
};

export const isVideoMediaLink = (value: unknown) =>
  videoExtensions.has(resolveMediaExtension(value));

export const isPdfMediaLink = (value: unknown) =>
  pdfExtensions.has(resolveMediaExtension(value));

const extractMediaLink = (value: unknown) => {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return "";

  const source = value as Record<string, unknown>;
  return (
    source["СсылкаS3"] ??
    source["ПутьКФайлу"] ??
    source["Ссылка"] ??
    source["url"] ??
    source["ПутьКФото"] ??
    ""
  );
};

const getMediaDisplayOrder = (value: unknown) => {
  if (!value || typeof value !== "object") return null;

  const order = parseNumber(
    (value as Record<string, unknown>)["ПорядокОтображения"],
  );

  return order > 0 ? order : null;
};

const sortMediaByDisplayOrder = (values: unknown[]) =>
  values
    .map((value, index) => ({
      value,
      index,
      order: getMediaDisplayOrder(value),
    }))
    .sort((a, b) => {
      if (a.order !== null && b.order !== null) return a.order - b.order;
      if (a.order !== null) return -1;
      if (b.order !== null) return 1;
      return a.index - b.index;
    })
    .map((item) => item.value);

const collectUniqueMediaLinks = (
  values: unknown[],
  seen = new Set<string>(),
) => {
  const links: string[] = [];

  values.forEach((value) => {
    const normalized = normalizeMediaUrl(extractMediaLink(value));
    if (!normalized) return;

    const fileName = resolveMediaFileName(normalized);
    const dedupeKey = fileName || normalized.toLowerCase();
    if (seen.has(dedupeKey)) return;

    seen.add(dedupeKey);
    links.push(normalized);
  });

  return links;
};

export const collectProductMediaLinks = (
  product: Record<string, unknown> | null | undefined,
  fallbackValues: unknown[] = [],
) => {
  if (!product) return [];

  const fileLinks = parseJsonArray(product["СсылкиФайлов"]);
  const fileEntries = parseJsonArray(product["Файлы"]);
  const attachments = sortMediaByDisplayOrder([...fileEntries, ...fileLinks]);
  const seenMedia = new Set<string>();

  const orderedMediaLinks = collectUniqueMediaLinks(
    attachments.filter((item) => extractMediaLink(item)),
    seenMedia,
  );
  const fallbackImageLinks = collectUniqueMediaLinks(fallbackValues, seenMedia);

  return [...orderedMediaLinks, ...fallbackImageLinks];
};

export const getPrimaryImageFromMedia = (mediaLinks: string[]) =>
  mediaLinks.find((item) => !isVideoMediaLink(item) && !isPdfMediaLink(item)) ??
  "";
